import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  planContentLabel,
  type PlanLibraryPlan,
} from 'hooks/planLibrary/planLibraryHooks';
import {
  matchPlansToIncident,
  type IncidentMatchContext,
} from 'hooks/planLibrary/matchPlansToIncident';

export function planHasContents(plan: PlanLibraryPlan): boolean {
  return (
    plan.tasks.length > 0 ||
    plan.forms.length > 0 ||
    plan.files.length > 0 ||
    plan.rosterTemplateIds.length > 0
  );
}

function GroupHeading({ children }: { children: string }) {
  return (
    <div className="tw:sticky tw:top-0 tw:z-10 tw:border-b tw:border-border tw:bg-muted tw:px-3 tw:py-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
      {children}
    </div>
  );
}

/** Plan list with plans tagged for this incident grouped first. */
export function PlanPickerList({
  plans,
  incident,
  selectedId,
  onSelect,
  className,
}: {
  plans: PlanLibraryPlan[];
  incident: IncidentMatchContext;
  selectedId: string | null;
  onSelect: (plan: PlanLibraryPlan) => void;
  className?: string;
}) {
  const { matches, others } = useMemo(
    () => matchPlansToIncident(plans, incident),
    [plans, incident]
  );

  const row = (plan: PlanLibraryPlan) => (
    <button
      key={plan.id}
      type="button"
      className={cn(
        'tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-0.5 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:text-left tw:last:border-b-0',
        plan.id === selectedId
          ? 'tw:bg-accent tw:text-accent-foreground'
          : 'tw:hover:bg-muted/50'
      )}
      onClick={() => onSelect(plan)}
    >
      <span className="tw:text-sm tw:font-medium">{plan.name}</span>
      <span className="tw:text-xs tw:text-muted-foreground">
        {planContentLabel(plan)}
      </span>
    </button>
  );

  return (
    <div
      className={cn(
        'tw:overflow-auto tw:rounded-md tw:border tw:border-border',
        className
      )}
    >
      {matches.length > 0 ? (
        <>
          <GroupHeading>Matches this incident</GroupHeading>
          {matches.map(row)}
          {others.length > 0 ? <GroupHeading>Other plans</GroupHeading> : null}
        </>
      ) : null}
      {others.map(row)}
    </div>
  );
}

/** What applying a plan brings in, beyond its task list. */
export function PlanExtrasPreview({ plan }: { plan: PlanLibraryPlan }) {
  const extras: string[] = [];
  if (plan.files.length > 0) {
    extras.push(
      `${plan.files.length} ${plan.files.length === 1 ? 'document' : 'documents'} copied into Files`
    );
  }
  if (plan.rosterTemplateIds.length > 0) {
    extras.push(
      `${plan.rosterTemplateIds.length} roster ${plan.rosterTemplateIds.length === 1 ? 'template' : 'templates'} added to the org chart`
    );
  }
  if (extras.length === 0 && plan.layers.length === 0) return null;

  return (
    <div className="tw:flex tw:flex-col tw:gap-1 tw:text-xs tw:text-muted-foreground">
      {extras.map((line) => (
        <span key={line}>{line}</span>
      ))}
      {plan.layers.length > 0 ? (
        <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
          Suggested map layers:
          {plan.layers.map((layer) => (
            <Badge key={`${layer.kind}:${layer.id}`} variant="outline">
              {layer.name}
            </Badge>
          ))}
        </span>
      ) : null}
    </div>
  );
}
